/**
 * Route Prefetcher Utility
 *
 * This utility helps with prefetching routes based on user navigation patterns
 * to improve perceived performance.
 */

// Map of route paths to their lazy-loaded components
const routeComponentMap: Record<string, () => Promise<any>> = {
  '/': () => import('../pages/HomePage'),
  '/login': () => import('../pages/LoginPage'),
  '/signup/buyer': () => import('../pages/BuyerSignupPage'),
  '/profile': () => import('../pages/ProfilePage').then(module => ({ default: module.ProfilePage })),
  '/cart': () => import('../pages/CartPage').then(module => ({ default: module.CartPage })),
  '/checkout': () => import('../pages/CheckoutPage'),
  '/seller/dashboard': () => import('../pages/SellerDashboard'),
  '/seller/create-listing': () => import('../pages/CreateListingPage'),
  '/manage-listings': () => import('../pages/ManageListingsPage'),
  '/search': () => import('../pages/SearchResultsPage'),
  '/listing': () => import('../pages/ListingDetailPage'),
  '/inbox': () => import('../pages/InboxPage'),
};

// Common route pairs - when a user visits the first route, prefetch the second
const routePairs: [string, string][] = [
  ['/cart', '/checkout'],
  ['/login', '/profile'],
  ['/login', '/signup/buyer'],
  ['/seller/dashboard', '/seller/create-listing'],
  ['/seller/dashboard', '/manage-listings'],
  ['/', '/search'],
  ['/', '/login'],
];

// Track prefetched routes to avoid duplicate prefetching
const prefetchedRoutes = new Set<string>();

/**
 * Prefetch a specific route
 * @param route The route path to prefetch
 */
export const prefetchRoute = (route: string): void => {
  if (prefetchedRoutes.has(route) || !routeComponentMap[route]) {
    return;
  }

  // Mark as prefetched immediately to prevent duplicate requests
  prefetchedRoutes.add(route);

  // Use requestIdleCallback if available, otherwise setTimeout
  const scheduleIdleTask = (window as any).requestIdleCallback ||
    ((cb: Function) => setTimeout(cb, 1000));

  scheduleIdleTask(() => {
    try {
      // Trigger the import but don't wait for it
      routeComponentMap[route]();
      console.log(`Prefetched route: ${route}`);
    } catch (error) {
      console.error(`Failed to prefetch route: ${route}`, error);
    }
  });
};

/**
 * Prefetch related routes based on the current route
 * @param currentRoute The current route path
 */
export const prefetchRelatedRoutes = (currentRoute: string): void => {
  // Find all route pairs where the current route is the first element
  const relatedRoutes = routePairs
    .filter(([first]) => first === currentRoute || first === '*' || currentRoute.includes(first))
    .map(([_, second]) => second);

  // Prefetch each related route
  relatedRoutes.forEach(route => {
    prefetchRoute(route);
  });
};

/**
 * Initialize route prefetching based on user interaction
 */
export const initRoutePrefetching = (): void => {
  // Prefetch on mouse hover over links
  document.addEventListener('mouseover', (event) => {
    const target = event.target as HTMLElement;
    const link = target.closest('a');

    if (link && link.href && link.href.startsWith(window.location.origin)) {
      const path = new URL(link.href).pathname;
      prefetchRoute(path);
    }
  });

  // Prefetch related routes when the page loads
  window.addEventListener('load', () => {
    const currentPath = window.location.pathname;
    prefetchRelatedRoutes(currentPath);
  });

  // Prefetch when the user is idle
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(() => {
      // Prefetch high-priority routes during idle time
      prefetchRoute('/');
      prefetchRoute('/login');
    });
  }
};

// Create a named object before exporting as default
const RoutePrefetcher = {
  prefetchRoute,
  prefetchRelatedRoutes,
  initRoutePrefetching,
};

export default RoutePrefetcher;
